<script lang="ts">
  // Reusable follow / following button. Drives state through the global
  // `following` store, so multiple instances of this button (profile,
  // account list, leaderboard row…) for the same username stay in sync
  // without the parent components knowing about each other.
  //
  // Hidden entirely when:
  //   - the viewer is anonymous (no auth → no follow possible; the
  //     login pill in the corner is their CTA)
  //   - the viewer is looking at their own username (no self-follow)
  // Returning null markup in those cases keeps callers from having to
  // gate the button themselves.
  //
  // States:
  //   - not following → "Follow"   (accent fill)
  //   - following     → "Following" (outline). Hover swaps in
  //                     "Unfollow @{username}" with a danger tint, so a
  //                     player who hovers gets a clear preview of what
  //                     a click will do.

  import {
    followUser,
    unfollowUser,
    following,
    type FollowToggleResponse,
  } from '../lib/stores/following';
  import { user } from '../lib/stores/auth';
  import { showToast } from '../lib/stores/toast';

  type Size = 'sm' | 'md' | 'lg';

  interface Props {
    username: string;
    size?: Size;
    /**
     * When true, suppress the hover-text-swap to "Unfollow @{username}"
     * — the button just shows "Follow" / "Following" at a steady width
     * regardless of state. Use this in dense layouts (table cells, list
     * rows) where the wider hover label would push neighbouring content
     * around as the cursor moves.
     */
    compact?: boolean;
    /**
     * Notified after a successful toggle with the server's authoritative
     * state + counts. Lets the host (e.g. a profile page) update its
     * displayed `followers_count` without a separate refetch.
     */
    onchange?: (res: FollowToggleResponse) => void;
  }

  let { username, size = 'md', compact = false, onchange }: Props = $props();

  let busy = $state(false);

  const following_ = $derived($following.has(username));
  const isOwnProfile = $derived($user?.username === username);
  const visible = $derived(!!$user && !isOwnProfile);

  async function toggle(): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      const res = following_
        ? await unfollowUser(username)
        : await followUser(username);
      onchange?.(res);
    } catch {
      showToast(
        following_ ? 'Could not unfollow. Try again.' : 'Could not follow. Try again.',
        'error',
      );
    } finally {
      busy = false;
    }
  }
</script>

{#if visible}
  <button
    type="button"
    class="follow-btn follow-btn--{size}"
    class:follow-btn--following={following_}
    class:follow-btn--compact={compact}
    onclick={toggle}
    disabled={busy}
    aria-label={following_ ? `Unfollow ${username}` : `Follow ${username}`}
  >
    {#if following_}
      <span class="follow-btn__label-default">Following</span>
      <span class="follow-btn__label-hover">Unfollow @{username}</span>
    {:else}
      <span class="follow-btn__label-default">Follow</span>
    {/if}
  </button>
{/if}
